import { parseUtcTimestamp } from './utcDate';
import {
    DRIVER_ACTIVITY_STATUS,
    getDriverActivityState,
} from './constants';

/** status_list / activity_status: inactive | active | pause | busy */
const END_STATUS = DRIVER_ACTIVITY_STATUS.ACTIVE;

const toTimestamp = (value) => parseUtcTimestamp(value);

/**
 * Builds pause or busy intervals from status_list records for one driver.
 * Flow: active → pause|busy → active (each ends when driver returns to active).
 */
const buildStatusIntervals = (records, targetStatus) => {
    const statusKey = String(targetStatus ?? '')
        .trim()
        .toLowerCase();
    const sorted = [...records].sort((a, b) => {
        const aTime = toTimestamp(a?.datetime) ?? 0;
        const bTime = toTimestamp(b?.datetime) ?? 0;
        return aTime - bTime;
    });

    const intervals = [];
    let openStart = null;
    let openCarId = null;

    sorted.forEach((record) => {
        const status = String(record?.status ?? '')
            .trim()
            .toLowerCase();
        const time = toTimestamp(record?.datetime);
        if (time == null) return;

        if (status === statusKey) {
            if (openStart == null) {
                openStart = time;
                openCarId =
                    record?.fk_car_id != null ? record.fk_car_id : null;
            }
            return;
        }

        // Interval ends when driver returns to active.
        if (status === END_STATUS && openStart != null) {
            intervals.push({
                start: openStart,
                end: time,
                carId: openCarId,
            });
            openStart = null;
            openCarId = null;
        }
    });

    return { intervals, openStart, openCarId };
};

/**
 * Formats a millisecond duration for display (Serbian-style short form).
 * Examples: "45 s", "9 min", "1 h 5 min"
 */
export const formatStatusDuration = (ms) => {
    const totalSeconds = Math.max(0, Math.floor(Number(ms) / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
    }
    if (minutes > 0) {
        return `${minutes} min`;
    }
    return `${seconds} s`;
};

/** Current pause/busy duration always in whole minutes (e.g. "0 min", "9 min", "75 min"). */
export const formatStatusMinutes = (ms) => {
    const totalMinutes = Math.max(0, Math.floor(Number(ms) / 60000));
    return `${totalMinutes} min`;
};

/**
 * Returns drivers currently in the given activity status (open interval),
 * with current and total durations for that status.
 *
 * @param {Array} statusList - GET /drivers/status_list records
 * @param {string} targetStatus - DRIVER_ACTIVITY_STATUS.PAUSE | BUSY
 * @param {number} [nowMs=Date.now()] - reference time for open intervals / live ticks
 */
export const getCurrentlyDriversByStatus = (
    statusList,
    targetStatus,
    nowMs = Date.now()
) => {
    if (!Array.isArray(statusList) || statusList.length === 0) {
        return [];
    }

    const byDriver = new Map();

    statusList.forEach((record) => {
        const driverId = record?.fk_driver_id;
        if (driverId == null) return;
        const key = String(driverId);
        if (!byDriver.has(key)) {
            byDriver.set(key, []);
        }
        byDriver.get(key).push(record);
    });

    const result = [];

    byDriver.forEach((records, driverKey) => {
        const { intervals, openStart, openCarId } = buildStatusIntervals(
            records,
            targetStatus
        );

        // Only drivers with an open interval (no closing `active` yet).
        if (openStart == null) {
            return;
        }

        const currentMs = Math.max(0, nowMs - openStart);
        let totalMs = currentMs;

        intervals.forEach((interval) => {
            totalMs += Math.max(0, interval.end - interval.start);
        });

        result.push({
            driverId: records[0]?.fk_driver_id ?? driverKey,
            carId: openCarId,
            currentStart: openStart,
            currentMs,
            totalMs,
        });
    });

    result.sort((a, b) => b.currentMs - a.currentMs);
    return result;
};

/**
 * Returns drivers currently on pause, with current and total pause durations.
 *
 * @param {Array} statusList - GET /drivers/status_list records
 * @param {number} [nowMs=Date.now()] - reference time for open intervals / live ticks
 */
export const getCurrentlyPausedDrivers = (statusList, nowMs = Date.now()) =>
    getCurrentlyDriversByStatus(
        statusList,
        DRIVER_ACTIVITY_STATUS.PAUSE,
        nowMs
    );

/**
 * Returns drivers currently busy, with current and total busy durations.
 *
 * @param {Array} statusList - GET /drivers/status_list records
 * @param {number} [nowMs=Date.now()] - reference time for open intervals / live ticks
 */
export const getCurrentlyBusyDrivers = (statusList, nowMs = Date.now()) =>
    getCurrentlyDriversByStatus(
        statusList,
        DRIVER_ACTIVITY_STATUS.BUSY,
        nowMs
    );

const ONLINE_STATUSES = new Set([
    DRIVER_ACTIVITY_STATUS.ACTIVE,
    DRIVER_ACTIVITY_STATUS.PAUSE,
    DRIVER_ACTIVITY_STATUS.BUSY,
]);

/** Merge status_list rows from multiple day fetches, deduped by id. */
export const mergeStatusListRecords = (...lists) => {
    const byKey = new Map();

    lists.flat().forEach((record) => {
        if (record == null) return;
        const key =
            record.id != null
                ? String(record.id)
                : `${record.fk_driver_id}|${record.datetime}|${record.status}`;
        byKey.set(key, record);
    });

    return Array.from(byKey.values());
};

/**
 * Start of the current working round from status_list:
 * last transition out of inactive (or first online status if none).
 */
export const getCurrentRoundStartMs = (records) => {
    if (!Array.isArray(records) || records.length === 0) {
        return null;
    }

    const sorted = [...records].sort((a, b) => {
        const aTime = toTimestamp(a?.datetime) ?? 0;
        const bTime = toTimestamp(b?.datetime) ?? 0;
        return aTime - bTime;
    });

    let roundStart = null;

    sorted.forEach((record) => {
        const status = String(record?.status ?? '')
            .trim()
            .toLowerCase();
        const time = toTimestamp(record?.datetime);
        if (time == null) return;

        if (status === DRIVER_ACTIVITY_STATUS.INACTIVE) {
            roundStart = null;
            return;
        }

        if (ONLINE_STATUSES.has(status) && roundStart == null) {
            roundStart = time;
        }
    });

    return roundStart;
};

/**
 * Drivers currently marked active on GET /drivers, with the time they
 * started the current working round. Inactive drivers are omitted.
 *
 * @param {Array} drivers - GET /drivers
 * @param {Array} statusList - GET /drivers/status_list records
 */
export const getCurrentlyActiveDrivers = (drivers, statusList) => {
    if (!Array.isArray(drivers) || drivers.length === 0) {
        return [];
    }

    const byDriver = new Map();
    if (Array.isArray(statusList)) {
        statusList.forEach((record) => {
            const driverId = record?.fk_driver_id;
            if (driverId == null) return;
            const key = String(driverId);
            if (!byDriver.has(key)) {
                byDriver.set(key, []);
            }
            byDriver.get(key).push(record);
        });
    }

    const result = [];

    drivers.forEach((driver) => {
        if (
            getDriverActivityState(driver?.activity_status) !==
            DRIVER_ACTIVITY_STATUS.ACTIVE
        ) {
            return;
        }
        const driverId = driver?.driver_id;
        if (driverId == null) return;

        const records = byDriver.get(String(driverId)) ?? [];
        result.push({
            driverId,
            startedAt: getCurrentRoundStartMs(records),
        });
    });

    result.sort((a, b) => {
        if (a.startedAt == null && b.startedAt == null) return 0;
        if (a.startedAt == null) return 1;
        if (b.startedAt == null) return -1;
        return a.startedAt - b.startedAt;
    });

    return result;
};
